// Shared quick add: Search keeps its price capsule; collections keep native prices.
// - The price moves into the image, in a capsule (top right). It is a copy of
//   Boost's own price, so the format and the sale/compare prices stay Boost's;
//   the original price is hidden with CSS (assets/search-quick-add.css).
// - An "Add to Cart" button under the title. Boost shows each color as its own
//   card (data-product has the card's variant and option values), so the
//   button works with that card's variants only (from /products/<handle>.js):
//   one variant is added right away; with several (sizes) the button turns
//   into the sizes, sold out ones disabled, and the chosen one is added.
//   After adding, the theme's cart drawer opens (same ajaxProduct:added event
//   as the theme's product forms), or the cart page if the theme uses it.
// Boost re-renders the cards on filter, sort and pagination changes, so the
// cards are enhanced again whenever the list changes.
(() => {
  if (document.documentElement.hasAttribute('data-sqa-ready')) return;
  document.documentElement.setAttribute('data-sqa-ready', '');
  const isCollection = document.body.classList.contains('template-collection');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const products = new Map();

  const productJson = (handle) => {
    if (!products.has(handle)) {
      const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`).then((response) => {
        if (!response.ok) throw new Error(`Product ${response.status}`);
        return response.json();
      });
      request.catch(() => products.delete(handle));
      products.set(handle, request);
    }
    return products.get(handle);
  };

  // Resolve charts on the server with the same Liquid snippet as the PDP.
  // Fetch only when sizes open; cache the response and load the dialog script once.
  const charts = new Map();
  let chartScript;
  const chartMarkup = (handle) => {
    if (!charts.has(handle)) {
      const request = fetch(`${root}products/${encodeURIComponent(handle)}?section_id=cro-quick-add-size-chart`)
        .then(async (response) => {
          if (!response.ok) throw new Error(`Size chart ${response.status}`);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const component = doc.querySelector('sleefs-size-chart');
          if (!component) return null;
          doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
            if (!Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some((existing) => existing.href === link.href)) {
              document.head.append(link.cloneNode(true));
            }
          });
          if (!customElements.get('sleefs-size-chart')) {
            if (!chartScript) {
              chartScript = new Promise((resolve, reject) => {
                const source = doc.querySelector('script[src]');
                if (!source) { reject(new Error('Size chart script missing')); return; }
                const script = document.createElement('script');
                script.src = source.src;
                script.onload = resolve;
                script.onerror = () => { script.remove(); reject(new Error('Size chart unavailable')); };
                document.head.append(script);
              }).catch((error) => { chartScript = null; throw error; });
            }
            await chartScript;
          }
          return component;
        });
      request.catch(() => charts.delete(handle));
      charts.set(handle, request);
    }
    return charts.get(handle);
  };

  const cardData = (card) => {
    try {
      if (!card.dataset.product && card.dataset.productHandle) {
        return { handle: card.dataset.productHandle, tags: JSON.parse(card.dataset.sqaTags || '[]'),
          available: card.dataset.sqaAvailable !== 'false', options_with_values: [] };
      }
      return JSON.parse(card.dataset.product);
    } catch (error) {
      return null;
    }
  };

  // Bundle/customizer parent variants cannot be added without their required configuration.
  const needsConfiguration = (data) => data && (
    (data.tags || []).some((tag) => /^(fly bundles|bundles|custom|customized|personalized)$/i.test(tag)) ||
    /(^|[-_])(custom|personalized|personalised)([-_]|$)|make-your-own/i.test(data.handle)
  );

  // The card's variants: the ones whose option values are all among the
  // card's values (e.g. every size of the card's color).
  const cardVariants = (product, data) => {
    const splitVariant = String(data.splitProduct) === 'true'
      ? product.variants.find((variant) => String(variant.id) === String(data.variantId)) : null;
    const colorIndex = product.options.findIndex((option) => /^colou?r$/i.test(option.name));
    const cardOptions = (data.options_with_values || []).map((option) => ({
      name: String(option.original_name || option.name).toLowerCase(),
      values: (option.values || []).map((value) => String(value.title)),
    }));
    const optionIndex = (name) => product.options.findIndex((option) => option.name.toLowerCase() === name);

    return product.variants.filter((variant) =>
      (!splitVariant || colorIndex === -1 || variant.options[colorIndex] === splitVariant.options[colorIndex]) &&
      cardOptions.every((option) => {
        const index = optionIndex(option.name);
        return index === -1 || option.values.includes(variant.options[index]);
      })
    );
  };

  // Label for each variant: the option values that change between the
  // card's variants (usually just the size).
  const variantLabels = (variants) => {
    const changing = variants[0].options
      .map((_, index) => index)
      .filter((index) => new Set(variants.map((variant) => variant.options[index])).size > 1);
    return variants.map((variant) => changing.map((index) => variant.options[index]).join(' / ') || variant.title);
  };

  const openCart = (variantId, item) => {
    if (window.theme && theme.settings && theme.settings.cartType === 'page') {
      window.location = (theme.routes && theme.routes.cartPage) || `${root}cart`;
      return;
    }
    // Same event the theme's product forms send: the cart drawer rebuilds and
    // opens. Its listeners read the variant from the form's [name="id"].
    const form = document.createElement('form');
    form.hidden = true;
    form.innerHTML = '<input type="hidden" name="id">';
    form.firstChild.value = variantId;
    document.body.appendChild(form);
    form.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: item }, bubbles: true }));
    form.remove();
  };

  const addToCart = async (variantId) => {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');
    return data;
  };

  class QuickAdd {
    constructor(card) {
      this.card = card;
      this.box = document.createElement('div');
      this.box.className = 'sqa-box';
      this.message = document.createElement('p');
      this.message.className = 'sqa-message';
      this.message.setAttribute('role', 'status');
      const data = cardData(card);
      if (needsConfiguration(data)) this.showProductLink(data);
      else this.showButton(data?.available === false ? 'Sold Out' : 'Add to Cart', data?.available === false);

      const info = card.querySelector('.boost-sd__product-info') || card;
      info.append(this.box, this.message);
    }

    showProductLink(data) {
      this.box.classList.remove('sqa-box--sizes');
      this.box.replaceChildren();
      const link = document.createElement('a');
      link.className = 'sqa-button sqa-choose-options';
      link.href = `${root}products/${encodeURIComponent(data.handle)}`;
      link.textContent = 'Choose Options';
      link.addEventListener('click', (event) => event.stopPropagation());
      this.box.append(link);
    }

    showButton(label = 'Add to Cart', disabled = false) {
      this.box.classList.remove('sqa-box--sizes');
      this.box.innerHTML = '';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sqa-button';
      button.textContent = label;
      button.disabled = disabled;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.start(button);
      });
      this.box.append(button);
      this.button = button;
    }

    async start(button) {
      const data = cardData(this.card);
      if (!data || !data.handle) return;
      this.setMessage('');
      button.disabled = true;
      button.classList.add('sqa-button--loading');

      try {
        const product = await productJson(data.handle);
        if (needsConfiguration(data) || needsConfiguration(product) || product.requires_selling_plan) {
          this.showProductLink(data);
          return;
        }
        const variants = cardVariants(product, data);
        if (variants.length > 1) {
          this.showSizes(variants, data.handle);
          return;
        }
        const variant = variants[0] || product.variants.find((item) => String(item.id) === String(data.variantId));
        if (!variant || !variant.available) {
          this.showButton('Sold Out', true);
          return;
        }
        await this.add(variant.id);
      } catch (error) {
        this.showButton();
        this.setMessage(error.message);
      }
    }

    showSizes(variants, handle) {
      const labels = variantLabels(variants);
      this.box.classList.add('sqa-box--sizes');
      this.box.innerHTML = '';
      variants.forEach((variant, index) => {
        const size = document.createElement('button');
        size.type = 'button';
        size.className = 'sqa-size';
        size.textContent = labels[index];
        size.disabled = !variant.available;
        size.setAttribute('aria-label', `Add ${labels[index]} to cart${variant.available ? '' : ' (sold out)'}`);
        size.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.box.querySelectorAll('.sqa-size').forEach((item) => {
            item.disabled = true;
          });
          size.classList.add('sqa-size--loading');
          this.add(variant.id).catch((error) => {
            this.showButton();
            this.setMessage(error.message);
          });
        });
        this.box.append(size);
      });
      const chartSlot = document.createElement('div');
      chartSlot.className = 'sqa-size-chart';
      // Boost makes the entire card clickable; the chart must stay on search.
      chartSlot.addEventListener('click', (event) => event.stopPropagation());
      this.box.append(chartSlot);
      chartMarkup(handle).then((component) => {
        if (component && chartSlot.isConnected) chartSlot.append(component.cloneNode(true));
      }).catch(() => {
        if (!chartSlot.isConnected) return;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'cro-size-chart-link';
        retry.textContent = 'Retry size chart';
        retry.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          retry.disabled = true;
          chartMarkup(handle).then((component) => {
            if (component && chartSlot.isConnected) chartSlot.replaceChildren(component.cloneNode(true));
          }).catch(() => { retry.disabled = false; });
        });
        chartSlot.append(retry);
      });
      const first = this.box.querySelector('.sqa-size:not(:disabled)');
      if (first) first.focus();
      QuickAdd.open = this;
    }

    async add(variantId) {
      let item;
      try {
        item = await addToCart(variantId);
      } catch (error) {
        document.dispatchEvent(new CustomEvent('cro:atc-error'));
        throw error;
      }
      this.showButton('Added!', true);
      openCart(variantId, item);
      setTimeout(() => this.showButton(), 1500);
    }

    close() {
      if (this.box.classList.contains('sqa-box--sizes')) this.showButton();
    }

    setMessage(text) {
      this.message.textContent = text;
    }
  }

  // Close the sizes when clicking anywhere else or pressing Escape.
  document.addEventListener('click', (event) => {
    if (event.target.closest('.cro-size-chart-dialog')) return;
    if (QuickAdd.open && !QuickAdd.open.box.contains(event.target)) {
      QuickAdd.open.close();
      QuickAdd.open = null;
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.querySelector('.cro-size-chart-dialog[open]')) return;
    if (event.key === 'Escape' && QuickAdd.open) {
      QuickAdd.open.close();
      QuickAdd.open = null;
    }
  });

  /*const addPriceCapsule = (card) => {
    const price = card.querySelector('.boost-sd__product-price-wrapper');
    const image = card.querySelector('.boost-sd__product-image-wrapper');
    if (!price || !image) return;
    const capsule = document.createElement('div');
    capsule.className = 'sqa-price';
    capsule.append(price.cloneNode(true));
    image.append(capsule);
  };*/
  
  const enhance = () => {
    const scope = isCollection ? (document.querySelector('main') || document) : document;
    scope.querySelectorAll('.boost-sd__product-item[data-product]').forEach((card) => {
      if (!card.querySelector('.sqa-box')) new QuickAdd(card);
    });
    if (isCollection) scope.querySelectorAll('.grid-product[data-product-handle][data-sqa-tags]').forEach((card) => {
      if (!card.querySelector('.sqa-box')) new QuickAdd(card);
    });
  };

  let frame;
  const observer = new MutationObserver(() => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(enhance);
  });

  const init = () => {
    enhance();
    observer.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
